import type { SurgeonTemplate, TemplateCptEntry } from '@/lib/templates/types';
import { getSecondaryDefaultModifiers } from '@/lib/modifiers/xepsu';
import type { CptSearchEntry } from '@/lib/cptSearch/catalog';

export interface SmartModifierOption {
  id: string;
  label: string;
  cptCode: string;
  modifier: string;
  description: string;
}

function normalizeCode(code: string | number | null | undefined): string {
  return String(code || '').trim().toUpperCase();
}

function titleCaseSource(source: string): string {
  if (source === 'cpt_catalog') return 'Catalog';
  return source
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

export function buildSmartModifierOptionFromSearchEntry(entry: CptSearchEntry): SmartModifierOption {
  const defaultModifier = getSecondaryDefaultModifiers(entry.defaultModifiers)[0] || 'XS';
  return {
    id: `search-${normalizeCode(entry.code)}`,
    label: entry.description,
    cptCode: normalizeCode(entry.code),
    modifier: `-${defaultModifier}`,
    description: `Added from CPT search — ${titleCaseSource(entry.source)}`,
  };
}

export function buildMasterTemplateCptCodeSet(
  template: Pick<SurgeonTemplate, 'secondary_cpts' | 'common_addons'> | null | undefined,
): Set<string> {
  const codes = new Set<string>();
  template?.secondary_cpts?.forEach(cpt => codes.add(normalizeCode(cpt.code)));
  template?.common_addons?.forEach(cpt => codes.add(normalizeCode(cpt.code)));
  codes.delete('');
  return codes;
}

export function addMasterTemplateCptCode(codes: ReadonlySet<string>, cptCode: string | number): Set<string> {
  const next = new Set(codes);
  const normalized = normalizeCode(cptCode);
  if (normalized) next.add(normalized);
  return next;
}

export function removeMasterTemplateCptCode(codes: ReadonlySet<string>, cptCode: string | number): Set<string> {
  const next = new Set(codes);
  next.delete(normalizeCode(cptCode));
  return next;
}

export function isSmartModifierInMasterTemplate(
  option: Pick<SmartModifierOption, 'cptCode'>,
  masterTemplateCptCodes: ReadonlySet<string>,
): boolean {
  return masterTemplateCptCodes.has(normalizeCode(option.cptCode));
}

export function updateTemplateSmartModifier(
  template: SurgeonTemplate,
  option: SmartModifierOption,
  enabled: boolean,
): SurgeonTemplate {
  const cptCode = normalizeCode(option.cptCode);
  const currentSecondary = template.secondary_cpts || [];

  if (!enabled) {
    return {
      ...template,
      secondary_cpts: currentSecondary.filter(cpt => normalizeCode(cpt.code) !== cptCode),
    };
  }

  if (currentSecondary.some(cpt => normalizeCode(cpt.code) === cptCode)) {
    return template;
  }

  const nextEntry: TemplateCptEntry = {
    code: cptCode,
    description: option.label,
    default_modifiers: getSecondaryDefaultModifiers([option.modifier]),
  };

  return {
    ...template,
    secondary_cpts: [...currentSecondary, nextEntry],
  };
}
